import React, { useRef, useState } from 'react';
import {
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useOutsideClick,
} from '@chakra-ui/react';
import { useDayzed } from 'dayzed';
import { format } from 'date-fns';

import { MonthNamesShort, WeekdayNamesShort } from './utils/calanderUtils';
import { CalendarPanel } from './components/calendarPanel';
import {
  DatepickerConfigs,
  DatepickerProps,
  OnDateSelected,
} from './utils/commonTypes';

export interface SingleDatepickerProps extends DatepickerProps {
  date: Date;
  configs?: DatepickerConfigs;
  disabled?: boolean;
  onDateChange: (date: Date) => void;
  id?: string;
  name?: string;
  isInvalid?: boolean;
  touched?: boolean;
  SetTouched?: (isTouched: boolean) => void;
  applying: false;
  h?: string;
  px?: string;
  borderRadius?: string;
  borderWidth?: string;
  focusStyles?: { boxShadow?: string; borderColor?: string };
  errorBorderColor?: string;
}

const DefaultConfigs = {
  dateFormat: 'do MMM yyyy',
  monthNames: MonthNamesShort,
  dayNames: WeekdayNamesShort,
};

export const SingleDatepicker: React.FC<SingleDatepickerProps> = ({
  configs = DefaultConfigs,
  propsConfigs,
  ...props
}) => {
  const {
    date,
    name,
    disabled,
    onDateChange,
    id,
    isInvalid = false,
    touched,
    SetTouched,
    applying,
    h,
    px,
    borderRadius,
    borderWidth,
    focusStyles,
    errorBorderColor,
  } = props;
  // chakra popover utils
  const ref = useRef<HTMLElement>(null);
  const initialFocusRef = useRef<HTMLInputElement>(null);

  const [popoverOpen, setPopoverOpen] = useState(false);

  useOutsideClick({
    ref,
    handler: () => setPopoverOpen(false),
  });

  // dayzed utils
  const handleOnDateSelected: OnDateSelected = ({
    selectable,
    date: selectedDate,
  }) => {
    if (!selectable) return;
    if (selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())) {
      onDateChange(selectedDate);
      setPopoverOpen(false);
    }
  };

  const dayzedData = useDayzed({
    showOutsideDays: true,
    onDateSelected: handleOnDateSelected,
    selected: date,
  });

  let formattedDate = '';
  if (date) {
    formattedDate = format(date, configs.dateFormat);
  }
  return (
    <Popover
      placement='bottom-start'
      variant='responsive'
      isOpen={popoverOpen}
      onClose={() => setPopoverOpen(false)}
      initialFocusRef={initialFocusRef}
      isLazy
    >
      <PopoverTrigger>
        <Input
          id={id}
          borderRadius={borderRadius}
          color='black'
          cursor='pointer'
          autoComplete='off'
          isDisabled={disabled}
          ref={initialFocusRef}
          onClick={() => setPopoverOpen(!popoverOpen)}
          name={name}
          value={formattedDate}
          isInvalid={isInvalid && (touched || applying)}
          onBlur={() => SetTouched(true)}
          onFocus={() => SetTouched(false)}
          onChange={e => e.target.value}
          h={h}
          px={px}
          borderWidth={borderWidth}
          _focus={focusStyles}
          errorBorderColor={errorBorderColor}
          {...propsConfigs?.inputProps}
        />
      </PopoverTrigger>
      <PopoverContent ref={ref} width='100%' borderRadius='0px' boxShadow='lg'>
        <PopoverBody>
          <CalendarPanel
            renderProps={dayzedData}
            configs={configs}
            propsConfigs={propsConfigs}
          />
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
